import { useCallback, useEffect, useRef, useState } from 'react';
import notify from '../../../utils/notify';
import useNegotiationApi from './useNegotiationApi';
import { pick } from '../../../shared/offer-domain/pick';

const POLL_MS = 10000;

const idOf = m => pick(m, 'id', 'Id');
const createdOf = m => pick(m, 'createdDate', 'CreatedDate');
const kindOf = m => Number(pick(m, 'kind', 'Kind') || 0);

// Appends without duplicates, in case `after` is inclusive on the BE.
const merge = (list, incoming) => {
  const seen = new Set(list.map(idOf));
  return [...list, ...incoming.filter(m => !seen.has(idOf(m)))];
};

// One chat thread per partner (be#451 D-11): load on open, then poll with
// `?after=<last createdDate>` every 10 s while the room is mounted and the
// tab is visible. A new system message (kind != 0: round, accept, withdraw,
// overdue, extend) means the other side acted, so `onSystemEvent` lets the
// page refetch the package.
const useNegotiationChat = (partnerId, { onSystemEvent } = {}) => {
  const { getMessages, postMessage } = useNegotiationApi();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastRef = useRef(null);
  const onEventRef = useRef(onSystemEvent);
  onEventRef.current = onSystemEvent;

  const apply = useCallback(incoming => {
    if (!incoming.length) return;
    setMessages(prev => merge(prev, incoming));
    lastRef.current = createdOf(incoming[incoming.length - 1]);
  }, []);

  const fetchNew = useCallback(async () => {
    if (!partnerId) return;
    const incoming = await getMessages(partnerId, { after: lastRef.current });
    apply(incoming);
    if (incoming.some(m => kindOf(m) !== 0)) onEventRef.current?.();
  }, [apply, getMessages, partnerId]);

  useEffect(() => {
    if (!partnerId) return undefined;
    let cancelled = false;
    lastRef.current = null;
    setMessages([]);
    setIsLoading(true);
    getMessages(partnerId)
      .then(list => !cancelled && apply(list))
      .catch(error => !cancelled && notify.apiError(error, 'load chat'))
      .finally(() => !cancelled && setIsLoading(false));

    const id = setInterval(() => {
      if (document.hidden) return;
      // Polling errors stay silent; the next tick retries.
      fetchNew().catch(() => {});
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [apply, fetchNew, getMessages, partnerId]);

  const send = useCallback(
    async text => {
      await postMessage(partnerId, text);
      await fetchNew();
    },
    [fetchNew, partnerId, postMessage]
  );

  return { messages, isLoading, send, refresh: fetchNew };
};

export default useNegotiationChat;
